import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Modal, Form, Input, message, Empty, Typography, Popconfirm, Upload, Space, Dropdown, Segmented, Select, Table } from 'antd';
import AppLoading from '../components/AppLoading';
import { PlusOutlined, EditOutlined, EyeOutlined, DeleteOutlined, DashboardOutlined, DownloadOutlined, UploadOutlined, DeleteRowOutlined, AppstoreOutlined, BarsOutlined, SearchOutlined, ThunderboltOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useDeployStore } from '../stores/deployStore';
import { fetchPages, createPage, deletePage } from '../api/pages';
import { exportPagesBackup, importPagesBackup, backupErrorHandler, backupExportErrorHandler, type BackupMode } from '../utils/backup';
import { fetchBackupPolicy, type BackupPolicyView } from '../api/backup';
import AiGenerateDrawer from '../components/ai-generate/AiGenerateDrawer';

const { Title, Text } = Typography;

type SortKey = 'updatedDesc' | 'updatedAsc' | 'name' | 'size';

export default function DashboardList() {
  const navigate = useNavigate();
  const [pages, setPages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [form] = Form.useForm();
  // 2026-08-31｜搜索 / 排序 / 视图切换（卡片|列表，localStorage 持久）
  const [keyword, setKeyword] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('updatedDesc');
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => (localStorage.getItem('dashboardListView') as 'card' | 'list') || 'card');
  // 导入导出权限（服务端权威；ZIP 默认仅超管可见，JSON 按套餐/额度）
  const [backupPolicy, setBackupPolicy] = useState<BackupPolicyView | null>(null);

  useEffect(() => {
    fetchBackupPolicy().then(setBackupPolicy).catch(() => setBackupPolicy(null));
  }, []);

  const exportMenuItems = useMemo(() => {
    const suffix = backupPolicy
      ? backupPolicy.json.quota < 0
        ? ' · 不限'
        : ` · 本月剩余 ${backupPolicy.json.remaining} 次`
      : '';
    const items: { key: string; label: string }[] = [
      { key: 'json', label: `JSON 元数据（页面/组件配置，体积小）${suffix}` },
    ];
    if (backupPolicy?.zipAllowed) items.push({ key: 'zip', label: 'ZIP 打包（含背景图等资源，跨环境可用）' });
    return items;
  }, [backupPolicy]);

  const loadList = () => {
    setLoading(true);
    fetchPages().then((data) => setPages(data)).catch(() => message.error('加载列表失败')).finally(() => setLoading(false));
  };

  useEffect(() => { loadList(); }, []);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    let list = pages.filter((p) => !kw || (p.name || '').toLowerCase().includes(kw) || (p.description || '').toLowerCase().includes(kw));
    const byName = (a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'zh');
    switch (sortKey) {
      case 'updatedAsc': list = [...list].sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()); break;
      case 'name': list = [...list].sort(byName); break;
      case 'size': list = [...list].sort((a, b) => (b.componentCount ?? 0) - (a.componentCount ?? 0)); break;
      default: list = [...list].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return list;
  }, [pages, keyword, sortKey]);

  /** 大屏预览快照：thumbnail > 背景图 > 占位 */
  const pageCoverSrc = (p: any): string | null => p.thumbnail || (p.config && (p.config as any).backgroundImage) || null;

  // 导出备份：JSON 元数据 / ZIP（含背景图等资源）两种模式按需选择（服务端强校验权限与额度）
  const handleExport = async (mode: BackupMode) => {
    try {
      setExporting(true);
      const n = await exportPagesBackup(mode);
      message.success(`已导出 ${n} 个大屏（${mode === 'zip' ? 'ZIP 含资源文件' : 'JSON 元数据'}）`);
      fetchBackupPolicy().then(setBackupPolicy).catch(() => {});
    } catch (e) {
      backupExportErrorHandler(e);
    } finally {
      setExporting(false);
    }
  };

  // 导入备份：支持 .json / .zip（zip 自动提取背景图等资源上传到当前环境）
  const handleImportFile = async (file: File) => {
    try {
      setImporting(true);
      const ok = await importPagesBackup(file);
      message.success(`已导入 ${ok} 个大屏`);
      loadList();
      return true;
    } catch (e) {
      backupErrorHandler(e);
      return false;
    } finally {
      setImporting(false);
    }
  };

  // 单个大屏导出（卡片级）：两种模式（ZIP 仅超管可见）
  const handleExportOne = async (id: string, mode: BackupMode) => {
    try {
      setExporting(true);
      const n = await exportPagesBackup(mode, id);
      message.success(`已导出「${pages.find((p) => p.id === id)?.name || '大屏'}」（${mode === 'zip' ? 'ZIP 含资源' : 'JSON 元数据'}）`);
      fetchBackupPolicy().then(setBackupPolicy).catch(() => {});
    } catch (e) {
      backupExportErrorHandler(e);
    } finally {
      setExporting(false);
    }
  };

  const handleCreate = async (values: { name: string; description?: string }) => {
    try {
      const page = await createPage(values);
      setPages((prev) => [page, ...prev]);
      setModalOpen(false);
      form.resetFields();
      message.success('创建成功');
      window.open(`/editor/dashboard/${page.id}`, '_blank');
    } catch { message.error('创建失败'); }
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePage(id);
      setPages((prev) => prev.filter((p) => p.id !== id));
      message.success('已移入回收站，30天后自动删除');
    } catch { message.error('删除失败'); }
  };

  if (loading) {
    return <AppLoading />;
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>可视化大屏</Title>
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="按名称/描述搜索"
            style={{ width: 220 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Select
            value={sortKey}
            onChange={(v) => setSortKey(v)}
            style={{ width: 130 }}
            options={[
              { value: 'updatedDesc', label: '时间↓ 新→旧' },
              { value: 'updatedAsc', label: '时间↑ 旧→新' },
              { value: 'name', label: '名称排序' },
              { value: 'size', label: '大小（组件数）' },
            ]}
          />
          <Segmented
            value={viewMode}
            onChange={(v) => { const m = v as 'card' | 'list'; setViewMode(m); localStorage.setItem('dashboardListView', m); }}
            options={[{ value: 'card', icon: <AppstoreOutlined />, label: '卡片' }, { value: 'list', icon: <BarsOutlined />, label: '列表' }]}
          />
          <Button icon={<DeleteRowOutlined />} onClick={() => navigate('/pages/trash')}>
            回收站
          </Button>
          <Dropdown
            menu={{
              items: exportMenuItems,
              onClick: ({ key }) => handleExport(key as BackupMode),
            }}
            disabled={exporting}
          >
            <Button icon={<DownloadOutlined />} loading={exporting}>
              导出备份 ▾
            </Button>
          </Dropdown>
          <Upload
            accept=".json,.zip,application/json,application/zip"
            showUploadList={false}
            beforeUpload={(file) => { handleImportFile(file); return false; }}
            disabled={importing}
          >
            <Button icon={<UploadOutlined />} loading={importing}>
              导入备份
            </Button>
          </Upload>
          <Button icon={<ThunderboltOutlined />} onClick={() => setAiOpen(true)}>
            AI 生成
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>新建大屏</Button>
        </Space>
      </div>

      <AiGenerateDrawer open={aiOpen} onClose={() => { setAiOpen(false); loadList(); }} />

      {filtered.length === 0 ? (
        <Empty description={keyword ? '没有匹配的大屏' : '暂无大屏，点击右上角新建'} style={{ marginTop: 80 }} />
      ) : viewMode === 'list' ? (
        <Table
          rowKey="id"
          size="middle"
          dataSource={filtered}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          columns={[
            { title: '名称', dataIndex: 'name', render: (v, r) => <a onClick={() => window.open(`/editor/dashboard/${r.id}`, '_blank')}>{v}</a> },
            { title: '描述', dataIndex: 'description', ellipsis: true, render: (v) => v || '—' },
            { title: '组件数', dataIndex: 'componentCount', width: 90, sorter: (a: any, b: any) => (a.componentCount ?? 0) - (b.componentCount ?? 0) },
            { title: '更新时间', dataIndex: 'updatedAt', width: 120, render: (v) => new Date(v).toLocaleString('zh-CN', { dateStyle: 'short', timeStyle: 'short' }) },
            {
              title: '操作', width: 210,
              render: (_, r) => (
                <Space size={4}>
                  <Button type="link" size="small" icon={<EditOutlined />} onClick={() => window.open(`/editor/dashboard/${r.id}`, '_blank')}>编辑</Button>
                  <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => window.open(`/preview/dashboard/${r.id}`, '_blank')}>预览</Button>
                  <Button type="link" size="small" icon={<CloudUploadOutlined />} onClick={() => useDeployStore.getState().openModal({ kind: 'dashboard', pageId: r.id, name: r.name })}>发布</Button>
                  <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => handleExportOne(r.id, 'zip')}>导出</Button>
                  <Popconfirm title="确认删除？" description="删除后将移入回收站，30天后自动清理" onConfirm={() => handleDelete(r.id)} okText="删除" cancelText="取消" okButtonProps={{ danger: true }}>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map((p) => {
            const coverSrc = pageCoverSrc(p);
            return (
              <Col key={p.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  cover={
                    <div style={{ height: 200, background: '#0f0f2e', overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                      onClick={() => window.open(`/preview/dashboard/${p.id}`, '_blank')}>
                      {coverSrc ? (
                        <img src={coverSrc} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <DashboardOutlined style={{ fontSize: 48, color: '#1890ff', opacity: 0.4 }} />
                        </div>
                      )}
                      {p.componentCount ? <span style={{ position: 'absolute', top: 6, right: 8, fontSize: 11, color: '#aaa', background: 'rgba(0,0,0,0.5)', padding: '1px 6px', borderRadius: 3 }}>{p.componentCount} 组件</span> : null}
                      {/* 半透明渐变遮罩 + 名称/描述/时间叠加在预览图底部（预览图窗口扩大到 actions 上方整块） */}
                      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '26px 12px 10px', background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0.82) 100%)' }}>
                        <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '20px' }}>
                          {p.name}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '18px', marginTop: 2 }}>
                          {p.description || '无描述'}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2 }}>
                          更新于 {new Date(p.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  }
                  actions={[
                    <Button type="link" icon={<EditOutlined />} onClick={() => window.open(`/editor/dashboard/${p.id}`, '_blank')}>编辑</Button>,
                    <Button type="link" icon={<EyeOutlined />} onClick={() => window.open(`/preview/dashboard/${p.id}`, '_blank')}>预览</Button>,
                    <Button type="link" icon={<CloudUploadOutlined />} onClick={() => useDeployStore.getState().openModal({ kind: 'dashboard', pageId: p.id, name: p.name })}>发布</Button>,
                    <Dropdown
                      key="export"
                      trigger={['click']}
                      disabled={exporting}
                      menu={{
                        items: [
                          { key: 'json', label: '导出 JSON（引用）' },
                          ...(backupPolicy?.zipAllowed ? [{ key: 'zip', label: '导出 ZIP（含资源）' }] : []),
                        ],
                        onClick: ({ key }) => handleExportOne(p.id, key as BackupMode),
                      }}
                    >
                      <Button type="link" icon={<DownloadOutlined />} onClick={(e) => e.preventDefault()}>
                        导出
                      </Button>
                    </Dropdown>,
                    <Popconfirm title="确认删除？" description="删除后将移入回收站，30天后自动清理" onConfirm={() => handleDelete(p.id)} okText="删除" cancelText="取消" okButtonProps={{ danger: true }}>
                      <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
                    </Popconfirm>,
                  ]}
                >
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Modal title="新建大屏" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="大屏名称" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} placeholder="描述（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
